import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ApiService } from '../../core/api.service';

export interface LeadAdminDto {
  id: string;
  fornecedor: { id: string; nome: string } | null;
  clienteName: string;
  clienteEmail: string;
  clientePhone: string;
  message: string;
  lgpdConsent: boolean;
  emailSent: boolean;
  isRead: boolean;
  createdAt: string;
}

@Injectable({ providedIn: 'root' })
export class LeadsService {
  constructor(private api: ApiService) {}

  /**
   * List leads with pagination and filters.
   * NOTE: Backend only supports: page, pageSize, fornecedorId, isRead
   * Additional filters like clienteEmail, dataInicio, dataFim are for client-side filtering only
   */
  list(params: {
    page?: number;
    pageSize?: number;
    fornecedorId?: string;
    isRead?: boolean;
    // Client-side only filters (not sent to backend):
    clienteEmail?: string;
    dataInicio?: string;
    dataFim?: string;
    emailSent?: boolean;
  } = {}): Observable<{ data: LeadAdminDto[]; meta: any }> {
    // Only send backend-supported parameters
    const backendParams: any = {
      page: params.page || 1,
      pageSize: params.pageSize || 20
    };
    
    if (params.fornecedorId !== undefined) {
      backendParams.fornecedorId = params.fornecedorId;
    }
    if (params.isRead !== undefined) {
      backendParams.isRead = params.isRead;
    }
    
    // Note: clienteEmail, dataInicio, dataFim, emailSent are NOT supported by backend
    // Components should filter these client-side if needed
    
    return (this.api.get('/admin/leads', backendParams) as Observable<any>).pipe(
      map((res: any) => {
        const rawData = res?.data ?? (Array.isArray(res) ? res : []);
        return {
          ...res,
          data: rawData.map((l: any) => this.normalizeLead(l)),
          meta: res?.meta ?? { total: rawData.length }
        };
      })
    );
  }

  get(id: string): Observable<LeadAdminDto> {
    return (this.api.get(`/admin/leads/${id}`) as Observable<any>).pipe(
      map((l: any) => this.normalizeLead(l))
    );
  }

  resend(id: string): Observable<any> {
    return this.api.post(`/admin/leads/${id}/resend`, {});
  }

  private normalizeLead(l: any): LeadAdminDto {
    // Handle both nested fornecedor object and flat fornecedorId/fornecedorNome/supplierName
    let fornecedor: { id: string; nome: string } | null = null;
    if (l.fornecedor) {
      fornecedor = l.fornecedor;
    } else {
      const id = l.fornecedorId || l.fornecedor_id || '';
      const nome = l.fornecedorNome || l.fornecedor_nome || l.supplierName || '';
      if (id || nome) {
        fornecedor = { id, nome };
      }
    }

    return {
      id: l.id,
      fornecedor,
      clienteName: l.clienteName || l.cliente_name || l.nomeCliente || l.noiva || l.brideName || '',
      clienteEmail: l.clienteEmail || l.cliente_email || l.emailCliente || l.email || '',
      clientePhone: l.clientePhone || l.cliente_phone || l.telefoneCliente || l.telefone || '',
      message: l.message || l.mensagem || '',
      lgpdConsent: l.lgpdConsent ?? l.lgpd_consent ?? l.lgpd ?? false,
      emailSent: l.emailSent ?? l.email_sent ?? false,
      isRead: l.isRead ?? l.is_read ?? l.lido ?? false,
      createdAt: l.createdAt || l.created_at || l.criadoEm || l.criado_em || '',
    };
  }
}
